import {Children, Fragment, ReactElement, cloneElement, useCallback, useMemo} from 'react';

import {envConfig} from 'config';
import {logger, renderUtils} from 'utils';

type InternalProps = {
    id?: string;
    attributeName?: string;
    children?: ReactElement | ReactElement[];
    skipIsHTMLChild?: boolean;
}

export type WithTestAttributeProps = {
    testAttribute?: InternalProps
}

type Props = InternalProps & WithTestAttributeProps;

export const TestAttribute: React.FC<Props> = (allProps) => {
    const props = useMemo(() => allProps?.testAttribute || allProps || {}, [allProps]);
    const {id, attributeName = 'data-testid', skipIsHTMLChild} = useMemo(() => props, [props]);
    const isEnabled = useMemo(() => envConfig.generateTestAttributes && id && attributeName, [id, attributeName]);

    const withTestAttribute: (nodes: React.ReactNode) => React.ReactNode = useCallback((nodes: React.ReactNode) => {
        const node = Children.only(nodes) as React.ReactElement;
        if (skipIsHTMLChild || renderUtils.isHTMLChild(node)) {
            return cloneElement(node, {[attributeName]: id});
        }
        logger.warn(`Unable to set a test attribute "${id}". Please check the syntax`);
        return node;
    }, [id, attributeName, skipIsHTMLChild]);

    return (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <Fragment>
            {isEnabled ? withTestAttribute(allProps.children) : allProps.children}
        </Fragment>
    );
};
